import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { cn } from '../../lib/classNameHelpers';
import { ButtonProps } from '../catalyst-ui-kit/button';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../catalyst-ui-kit/dropdown';
import { Tooltip } from '../Tooltip/Tooltip';

export interface SelectOption<T = string> {
  value: T;
  label: string;
  dropdownLabel?: string;
  tooltip?: React.ReactNode;
  disabled?: boolean;
}

interface SimpleSelectProps<T = string> extends Omit<ButtonProps, 'onChange'> {
  options: readonly SelectOption<T>[];
  selected?: SelectOption<T>;
  placeholder?: string;
  onChange: (value?: SelectOption<T>) => void;
  className?: string;
  dropdownClassName?: string;
}

export const SimpleSelect = <T = string,>({
  options,
  selected,
  placeholder,
  onChange,
  dropdownClassName,
  ...props
}: SimpleSelectProps<T>) => {
  return (
    <Dropdown>
      <DropdownButton
        aria-label={selected?.label}
        {...props}
        className={cn(
          'flex items-center group',
          props.disabled && 'cursor-not-allowed opacity-50',
          props.className
        )}
      >
        <span>{selected?.label ?? placeholder ?? 'Select'}</span>
        <ChevronDownIcon className="size-4 text-[--btn-icon] transition-transform group-data-[open]:-rotate-180" />
      </DropdownButton>
      <DropdownMenu anchor="bottom end" className={cn(dropdownClassName, 'z-20')}>
        {options.map((option, index) => (
          <DropdownItem
            key={index}
            onClick={() => onChange(option)}
            disabled={option.disabled}
            className={cn({
              'cursor-not-allowed': option.disabled,
              'bg-backgroundHoverOverlay': selected?.value === option.value,
            })}
          >
            {option.tooltip ? (
              <Tooltip content={option.tooltip}>{option.dropdownLabel || option.label}</Tooltip>
            ) : (
              option.dropdownLabel || option.label
            )}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};

/**
 * Version of SimpleSelect that has been styled to
 * look like <Button color="tertiary" />
 */
export const PlainSelect = <T,>({
  options,
  selected,
  onChange,
  className,
}: {
  options: readonly SelectOption<T>[];
  selected?: SelectOption<T>;
  onChange: (value?: SelectOption<T>) => void;
  className?: string;
}) => (
  <SimpleSelect
    options={options}
    selected={selected}
    onChange={onChange}
    className={cn(
      'relative inline-flex items-center gap-1 rounded transition-colors text-contentSecondary px-2 py-1 text-sm',
      'focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-blue-500',
      'bg-backgroundTertiary',
      'hover:after:absolute hover:after:inset-0 hover:after:rounded hover:after:bg-white/10 data-[hover]:bg-backgroundTertiary',
      'dark:hover:after:bg-white/5',
      'w-fit cursor-pointer text-left font-medium',
      className
    )}
  />
);
